import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import MobileFrame from "../../components/MobileFrame";

import { sendOtp } from "../../api/auth.api";
import { ApiError } from "../../api/ApiError";

import type {
    RootState,
    AppDispatch,
} from "../../store";

import { setOtpSession } from "../../store/authSlice";

const RESEND_TIMER_KEY = "otp_resend_available_at";
const RESEND_WAIT_SECONDS = 180;


const LoginPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [consentLegal, setConsentLegal] = useState(false);
    const [consentCommunication, setConsentCommunication] = useState(false);


    // --------------------------------------------------
    // Journey
    // --------------------------------------------------

    const journey = useSelector(
        (state: RootState) => state.journey.journey
    );

    const mobile =
        journey?.customerMobileMasked ?? "";


    // --------------------------------------------------
    // Form validation
    // --------------------------------------------------

    const isFormValid =
        Boolean(mobile) &&
        consentLegal &&
        consentCommunication;

    // --------------------------------------------------
    // Login / Send OTP
    // --------------------------------------------------

    const handleLogin = async () => {
        // Explicit double-submit protection.
        if (loading) return;

        // Journey is required to initiate OTP.
        if (!journey) {
            setError(
                "Your application session is unavailable. Please open the application link again."
            );
            return;
        }

        if (!isFormValid) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Request OTP from backend.
            const response = await sendOtp(
                journey.journeyId
            );

            // Store OTP session in Redux.
            //
            // IMPORTANT:
            // The backend should NOT return devOtp in production.
            // The frontend check below is only an additional safeguard.
            dispatch(
                setOtpSession({
                    otpToken:
                        response.data.otpToken,

                    expiresInSeconds:
                        response.data.expiresInSeconds,

                    devOtp:
                        import.meta.env.DEV
                            ? response.data.devOtp
                            : undefined,
                })
            );

            /*
             * Start the resend cooldown now — this is
             * when the OTP is actually sent. OtpPage reads
             * this same key, so the countdown is correct
             * the instant it mounts, instead of showing
             * "Resend OTP" with no timer.
             */
            sessionStorage.setItem(
                RESEND_TIMER_KEY,
                String(
                    Date.now() +
                    RESEND_WAIT_SECONDS * 1000
                )
            );

            // Move to OTP screen only after
            // successful OTP session creation.
            navigate(
                "/nverify-otp",
                {
                    replace: true,
                }
            );

        } catch (error) {
            let message =
                "Unable to send OTP. Please try again.";

            if (error instanceof ApiError) {

                if (error.status === 500) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() ||
                        message;
                }

            } else if (error instanceof Error) {
                message =
                    error.message?.trim() ||
                    message;
            }

            setError(message);
        } finally {
            setLoading(false);
        }
    };


    // --------------------------------------------------
    // Consent handlers
    // --------------------------------------------------

    const handleLegalConsentChange = (checked: boolean) => {
        setConsentLegal(checked);
        if (error) setError(null);
    };

    const handleCommunicationConsentChange = (checked: boolean) => {
        setConsentCommunication(checked);
        if (error) setError(null);
    };


    // --------------------------------------------------
    // UI
    // --------------------------------------------------

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                {/* Background decoration */}
                <div
                    aria-hidden="true"
                    className="absolute w-64 h-64 bg-purple-200 rounded-full top-[-90px] left-[-90px] blur-3xl opacity-20"
                />

                <div
                    aria-hidden="true"
                    className="absolute w-64 h-64 bg-blue-200 rounded-full bottom-[-120px] right-[-80px] blur-3xl opacity-20"
                />


                <div className="relative z-10 flex flex-col h-full px-6 pt-15">

                    {/* ==================================================
                        Header / Illustration
                    ================================================== */}

                    <div className="text-center mb-6">

                        {/* Decorative illustration */}
                        <div
                            aria-hidden="true"
                            className="relative flex justify-center mb-13 -mt-7"
                        >
                            <div className="relative w-60 h-48">

                                <div className="absolute inset-[-90px]">

                                    <div className="absolute w-72 h-40 bg-gradient-to-r from-blue-100 via-indigo-100 to-purple-100 opacity-60 blur-3xl rounded-full top-6 left-[-40px]" />

                                    <div className="absolute w-full h-full opacity-40">

                                        <svg
                                            viewBox="0 0 200 120"
                                            className="w-full h-full"
                                            aria-hidden="true"
                                            focusable="false"
                                        >
                                            <path
                                                d="M0,60 C40,20 80,100 120,60 C150,30 170,80 200,50"
                                                stroke="#6366f1"
                                                strokeWidth="2"
                                                fill="transparent"
                                                className="opacity-40"
                                            />

                                            <path
                                                d="M0,80 C50,40 90,110 140,70 C170,40 190,90 200,70"
                                                stroke="#3b82f6"
                                                strokeWidth="2"
                                                fill="transparent"
                                                className="opacity-30"
                                            />
                                        </svg>

                                    </div>
                                </div>


                                {/* Decorative card */}
                                <div className="absolute w-24 h-14 bg-green-100 border border-green-200 rounded-lg shadow-sm rotate-[10deg] bottom-16 right-4">

                                    <div className="absolute w-full h-2 bg-green-300 top-5 opacity-70" />

                                    <div className="absolute w-10 h-1 bg-green-400 rounded-full top-2 left-3 opacity-60" />

                                    <div className="absolute w-12 h-1 bg-green-400 rounded-full bottom-2 left-3 opacity-50" />

                                </div>


                                {/* Decorative rupee */}
                                <div className="absolute w-5 h-5 rounded-full bg-gradient-to-br from-yellow-200 to-yellow-400 shadow-md flex items-center justify-center top-12 right-10 animate-bounce">

                                    <span className="text-xs font-bold text-yellow-900">
                                        ₹
                                    </span>

                                    <div className="absolute w-full h-full bg-yellow-100 rounded-full blur-sm opacity-30" />

                                </div>


                                {/* Decorative rupee */}
                                <div className="absolute w-5 h-5 rounded-full bg-gradient-to-br from-yellow-200 to-yellow-400 shadow-md flex items-center justify-center bottom-16 right-4">

                                    <span className="text-xs font-bold text-yellow-900">
                                        ₹
                                    </span>

                                    <div className="absolute w-full h-full bg-yellow-100 rounded-full blur-sm opacity-30" />

                                </div>

                            </div>
                        </div>


                        {/* Brand */}
                        <div className="-mt-8 flex flex-col items-center justify-center">

                            <img
                                src="/CleverPeLogos/CleverPe_Black_H.svg"
                                alt="CleverPe"
                                className="h-8 object-contain"
                            />

                            <p className="mt-2 text-sm text-center bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 text-transparent bg-clip-text font-semibold">
                                India's First Intelligent Commerce Platform
                            </p>

                        </div>

                    </div>


                    {/* ==================================================
                        Login Card
                    ================================================== */}

                    <div className="-mt-4 p-5 bg-white border border-gray-100 shadow-sm rounded-2xl">

                        <h2 className="mb-4 text-medium font-semibold text-gray-800">
                            Login to continue
                        </h2>


                        {/* Mobile number */}
                        <div className="mb-4">

                            <div className="flex mt-1 overflow-hidden bg-gray-50 border border-gray-200 rounded-xl">

                                <span
                                    aria-hidden="true"
                                    className="px-3 py-3 text-medium text-gray-500 bg-gray-100 border-r border-gray-100"
                                >
                                    +91
                                </span>

                                <input
                                    value={mobile}
                                    readOnly
                                    disabled
                                    type="tel"
                                    aria-label="Registered mobile number"
                                    className="w-full px-3 py-3 text-medium bg-gray-50 !text-black outline-none cursor-not-allowed disabled:opacity-100"
                                />

                            </div>

                        </div>

                        {/* ==================================================
    Consents
================================================== */}

                        <div className="mt-4 mb-4 space-y-3 max-h-[250px] overflow-y-auto pr-1">

                            {/* Terms & Privacy + Data Processing consent */}
                            <label className="flex items-start gap-2 text-[10px] text-gray-500 leading-snug cursor-pointer">

                                <input
                                    type="checkbox"
                                    checked={consentLegal}
                                    onChange={(e) =>
                                        handleLegalConsentChange(e.target.checked)
                                    }
                                    className="mt-0.5 shrink-0 accent-indigo-500"
                                />

                                <span>
                                    By clicking “Continue”, I agree to the{" "}
                                    <span className="text-indigo-500 font-medium">
                                        Terms &amp; Conditions
                                    </span>{" "}
                                    and{" "}
                                    <span className="text-indigo-500 font-medium">
                                        Privacy Policy
                                    </span>{" "}
                                    and authorize CleverPe Technologies Private Limited
                                    (“CleverPe”) and its partner lenders to collect, access,
                                    process and securely store my personal and credit
                                    information for identity verification, credit assessment,
                                    eligibility evaluation, and processing and servicing
                                    products and services requested by me. I authorize
                                    CleverPe and its partner lenders to obtain my credit
                                    information from Credit Information Companies and share
                                    my information with authorised service providers and
                                    other entities as required for processing, assessing and
                                    servicing my applications, in accordance with applicable
                                    law and the Privacy Policy.
                                </span>

                            </label>


                            {/* Communication consent */}
                            <label className="flex items-start gap-2 text-[10px] text-gray-500 leading-snug cursor-pointer">

                                <input
                                    type="checkbox"
                                    checked={consentCommunication}
                                    onChange={(e) =>
                                        handleCommunicationConsentChange(e.target.checked)
                                    }
                                    className="mt-0.5 shrink-0 accent-indigo-500"
                                />

                                <span>
                                    I consent to receive communications regarding products
                                    and services through SMS, calls, WhatsApp and email,
                                    and understand that I may opt out of marketing
                                    communications at any time.
                                </span>

                            </label>

                        </div>


                        {/* ==================================================
                            Error
                        ================================================== */}

                        {error && (
                            <p
                                role="alert"
                                aria-live="polite"
                                className="mb-3 text-center text-xs text-red-500"
                            >
                                {error}
                            </p>
                        )}


                        {/* ==================================================
                            Continue
                        ================================================== */}

                        <button
                            type="button"
                            onClick={handleLogin}
                            disabled={
                                !isFormValid ||
                                loading
                            }
                            aria-busy={loading}
                            className="
                                w-full
                                py-3
                                text-m
                                font-semibold
                                text-white
                                transition
                                rounded-full
                                bg-gradient-to-r
                                from-indigo-500
                                to-blue-400
                                hover:opacity-90
                                active:scale-[0.99]
                                disabled:opacity-50
                                disabled:cursor-not-allowed
                            "
                        >
                            {loading
                                ? "Sending OTP..."
                                : "Continue"}
                        </button>

                    </div>


                    {/* ==================================================
                        Footer
                    ================================================== */}

                    <div className="flex-1" />

                    {/* <p className="pb-4 mt-4 text-[10px] text-center text-gray-400">

                        By continuing, you agree to our{" "}

                        <br />

                        <span className="text-indigo-500 font-medium">
                            Terms &amp; Conditions
                        </span>

                        {" "}and{" "}

                        <span className="text-indigo-500 font-medium">
                            Privacy Policy
                        </span>

                    </p> */}

                </div>

            </div>
        </MobileFrame>
    );
};

export default LoginPage;